import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  use,
  useCallback,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { ProjectType } from "reedy-data/models";
import { useProject } from "~/hooks/use-project";
import { IProduct, IProject } from "~/integrations/firebase/collections";
import { useAnalytics } from "~/contexts/analytics-context";
import { useProductActions } from "~/hooks/use-product-actions";
import { useProject as useProjectContext } from "~/contexts/project-context";

interface IProjectDetailsContext
  extends Pick<
    ReturnType<typeof useProductActions>,
    "generateKeywords" | "generateDescriptions" | "generateThemaCodes"
  > {
  exportModalOpen: boolean;
  setExportModalOpen: Dispatch<SetStateAction<boolean>>;
  productsToExport: IProduct[];
  setProductsToExport: (prodToExport: IProduct[]) => void;
  handleBack: () => void;
  isTrendProject: boolean | undefined;
  onRowClickCallback: (row: IProduct) => void;
  isLoadingRead: boolean;
  isLoadingUpdate: boolean;
  errorRead: Error | undefined;
  errorUpdate: Error | undefined;
  updateProjectName: (idProject: string, name: string) => void;
  setAmplifyProjectId: (idProject: string, amplifyProjectId: string) => void;
  products: IProduct[] | undefined;
  isLoadingGenerate: boolean;
  errorGenerate: Error | undefined;
  isLoadingProducts: boolean;
  errorProductsRead: Error | undefined;
  errorProjectRead: string | undefined;
  project?: IProject | null;
  idProject?: string;
}

const ProjectDetailsContext = createContext<IProjectDetailsContext>(
  undefined as never,
);

export const useProjectDetails = () => use(ProjectDetailsContext);

export const ProjectDetailsContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const { gaEvent } = useAnalytics();
  const { projectId: idProject } = useProjectContext();
  const [productsToExport, setProductsToExport] = useState([] as IProduct[]);

  const navigate = useNavigate();

  const {
    project,
    isLoadingRead,
    isLoadingUpdate,
    errorProjectRead,
    errorRead,
    errorUpdate,
    updateProjectName,
    setAmplifyProjectId,
    products,
    isLoadingProducts,
    errorProductsRead,
  } = useProject(idProject);

  const {
    generateKeywords,
    generateDescriptions,
    generateThemaCodes,
    isLoadingGenerate,
    errorGenerate,
  } = useProductActions();

  const handleBack = useCallback(() => {
    navigate({ to: "/projects" });
    gaEvent({
      type: "view_item_list",
      payload: {
        item_list_name: "projects",
        item_list_id: project?.id,
        item_list_category: project?.type,
      },
    });
  }, [gaEvent, navigate, project?.id, project?.type]);

  const isTrendProject =
    project?.type &&
    project?.type in [ProjectType.GOOGLE_TRENDS, ProjectType.AMAZON_TRENDS];

  const onRowClickCallback = useCallback(
    (row: IProduct) => {
      if (!idProject) {
        return;
      }

      navigate({
        to: "/projects/$projectId/product/$productId",
        params: { projectId: idProject, productId: row.id },
      });
      gaEvent({
        type: "view_item",
        payload: {
          item_id: row.id,
          item_name: row.title,
          item_category: "product",
          author: row.author,
          isbn: row.isbn,
          title: row.title,
        },
      });
    },
    [gaEvent, idProject, navigate],
  );

  const changeExports = useCallback(
    (prodToExport: IProduct[]) => setProductsToExport(prodToExport),
    [],
  );

  const contextValue: IProjectDetailsContext = useMemo(
    () => ({
      exportModalOpen,
      setExportModalOpen,
      productsToExport,
      setProductsToExport: changeExports,
      handleBack,
      isTrendProject,
      onRowClickCallback,
      isLoadingRead,
      isLoadingUpdate,
      errorRead,
      errorUpdate,
      updateProjectName,
      setAmplifyProjectId,
      products,
      generateKeywords,
      generateDescriptions,
      generateThemaCodes,
      isLoadingGenerate,
      errorGenerate,
      isLoadingProducts,
      errorProductsRead,
      errorProjectRead,
      project,
      idProject,
    }),
    [
      exportModalOpen,
      productsToExport,
      changeExports,
      handleBack,
      isTrendProject,
      onRowClickCallback,
      isLoadingRead,
      isLoadingUpdate,
      errorRead,
      errorUpdate,
      updateProjectName,
      setAmplifyProjectId,
      products,
      generateKeywords,
      generateDescriptions,
      generateThemaCodes,
      isLoadingGenerate,
      errorGenerate,
      isLoadingProducts,
      errorProjectRead,
      errorProductsRead,
      project,
      idProject,
    ],
  );

  return (
    <ProjectDetailsContext value={contextValue}>
      {children}
    </ProjectDetailsContext>
  );
};
